import { Box, Tabs, Text } from "@mantine/core";
import { Alert } from "@xplatform/design-system";
import type { FlowStepViewModel, FlowConfigViewModel } from "@models/doctype";
import { useStepEdit } from "@hooks/flow";
import { useTranslation } from "@hooks/useTranslation";
import { translateStepType } from "@utils/flowTypeTranslations";
import { LLMConfigTab } from "../LLMConfigTab";
import { OCRConfigTab } from "../OCRConfigTab";
import { StepRuntimeOptionsFields } from "../StepRuntimeOptionsFields";
import { CodeEditor } from "../CodeEditor";
import { EditorDrawer, TabLabel } from "../EditorDrawer";

interface StepEditDialogProps {
    open: boolean;
    onClose: () => void;
    /** Called with the new version the save created. */
    onSaved?: (config: FlowConfigViewModel) => void;
    step: FlowStepViewModel;
    docTypeId: string;
    flowConfig: FlowConfigViewModel;
    /** The version number a save will create. */
    nextVersion: number;
    canEdit?: boolean;
    initialTab?: string;
}

export const StepEditDialog = ({
    open,
    onClose,
    onSaved,
    step,
    docTypeId,
    flowConfig,
    nextVersion,
    canEdit = true,
    initialTab,
}: StepEditDialogProps) => {
    const { t } = useTranslation();

    const {
        activeTab,
        hasChanges,
        providers,
        models,
        selectedProvider,
        modelName,
        ocrModelName,
        isLoadingProviders,
        isLoadingModels,
        isLoading,
        saveError,
        clearError,
        validation,
        isValid,
        isMultiAgent,
        systemPrompt,
        userPrompt,
        temperature,
        topP,
        topK,
        maxOutputTokens,
        thinkingBudget,
        seed,
        runtimeConfigOptions,
        stepRuntimeOptions,
        responseSchema,
        ocrOptions,
        setActiveTab,
        setSystemPrompt,
        setUserPrompt,
        setTemperature,
        setTopP,
        setTopK,
        setMaxOutputTokens,
        setThinkingBudget,
        setSeed,
        setRuntimeConfigOptions,
        setStepRuntimeOptions,
        setResponseSchema,
        setOcrOptions,
        handleProviderChange,
        handleModelChange,
        handleSave,
        markAsChanged,
    } = useStepEdit({ open, step, flowConfig, docTypeId, initialTab });

    const onSave = async () => {
        const result = await handleSave();
        if (result) {
            onSaved?.(result);
            onClose();
        }
    };

    const withChange = <T,>(setter: (value: T) => void) => (value: T) => {
        setter(value);
        markAsChanged();
    };

    return (
        <EditorDrawer
            opened={open}
            onClose={onClose}
            title={
                <Box>
                    <Text fw={600} size="lg">
                        {t("admin.flowConfigs.labels.stepTitle", {
                            order: step.stepOrder,
                            type: translateStepType(step.stepType, t),
                        })}
                    </Text>
                    <Text size="xs" c="dimmed">
                        {t("admin.flowConfigs.editor.editingVersion", {
                            version: flowConfig.version,
                            next: nextVersion,
                        })}
                    </Text>
                </Box>
            }
            dirty={hasChanges}
            saving={isLoading}
            error={saveError}
            onDismissError={clearError}
            saveLabel={t("admin.flowConfigs.editor.saveAsVersion", { version: nextVersion })}
            blockedReason={hasChanges && !isValid ? t("admin.flowConfigs.editor.fixErrors") : null}
            onSave={onSave}
            canEdit={canEdit}
        >
            {step.llmStepConfig && (
                <Tabs value={activeTab} onChange={(value) => setActiveTab(value || "0")} keepMounted={false}>
                    <Tabs.List>
                        <Tabs.Tab value="0">
                            <TabLabel label={t("admin.flowConfigs.labels.llmConfig")} invalid={validation.model} />
                        </Tabs.Tab>
                        <Tabs.Tab value="1">
                            <TabLabel label={t("admin.flowConfigs.labels.systemPrompt")} invalid={validation.systemPrompt} />
                        </Tabs.Tab>
                        <Tabs.Tab value="2">
                            <TabLabel label={t("admin.flowConfigs.labels.userPrompt")} invalid={validation.userPrompt} />
                        </Tabs.Tab>
                        <Tabs.Tab value="3">
                            <TabLabel label={t("admin.flowConfigs.labels.responseSchema")} invalid={!!validation.responseSchema} />
                        </Tabs.Tab>
                        <Tabs.Tab value="4">
                            <TabLabel label={t("admin.flowConfigs.labels.runtimeOptions")} />
                        </Tabs.Tab>
                    </Tabs.List>

                    <Tabs.Panel value="0" className="editor-drawer__panel">
                        <LLMConfigTab
                            stepType={step.stepType}
                            isLoadingProviders={isLoadingProviders}
                            isLoadingModels={isLoadingModels}
                            providers={providers}
                            models={models}
                            selectedProvider={selectedProvider}
                            modelName={modelName}
                            temperature={temperature}
                            topP={topP}
                            topK={topK}
                            maxOutputTokens={maxOutputTokens}
                            thinkingBudget={thinkingBudget}
                            seed={seed}
                            configOptions={runtimeConfigOptions}
                            onProviderChange={handleProviderChange}
                            onModelChange={handleModelChange}
                            onTemperatureChange={withChange(setTemperature)}
                            onTopPChange={withChange(setTopP)}
                            onTopKChange={withChange(setTopK)}
                            onMaxOutputTokensChange={withChange(setMaxOutputTokens)}
                            onThinkingBudgetChange={withChange(setThinkingBudget)}
                            onSeedChange={withChange(setSeed)}
                            onConfigOptionsChange={setRuntimeConfigOptions}
                            onConfigOptionsDirty={markAsChanged}
                        />
                    </Tabs.Panel>

                    <Tabs.Panel value="1" className="editor-drawer__panel">
                        <CodeEditor
                            language="text"
                            label={t("admin.flowConfigs.labels.systemPrompt")}
                            value={systemPrompt}
                            onChange={withChange(setSystemPrompt)}
                            required
                            error={validation.systemPrompt ? t("admin.flowConfigs.editor.required") : null}
                        />
                    </Tabs.Panel>

                    <Tabs.Panel value="2" className="editor-drawer__panel">
                        {isMultiAgent && (
                            <Alert color="blue" mb="md">{t("admin.flowConfigs.editor.unusedWithAgents")}</Alert>
                        )}
                        <CodeEditor
                            language="text"
                            label={t("admin.flowConfigs.labels.userPrompt")}
                            value={userPrompt}
                            onChange={withChange(setUserPrompt)}
                            required={!isMultiAgent}
                            error={validation.userPrompt ? t("admin.flowConfigs.editor.required") : null}
                        />
                    </Tabs.Panel>

                    <Tabs.Panel value="3" className="editor-drawer__panel">
                        {isMultiAgent && (
                            <Alert color="blue" mb="md">{t("admin.flowConfigs.editor.unusedWithAgents")}</Alert>
                        )}
                        <CodeEditor
                            language="json"
                            label={t("admin.flowConfigs.labels.responseSchema")}
                            description={t("admin.flowConfigs.editor.schemaOptional")}
                            value={responseSchema}
                            onChange={withChange(setResponseSchema)}
                            placeholder='{"type": "object", "properties": {...}}'
                        />
                    </Tabs.Panel>

                    <Tabs.Panel value="4" className="editor-drawer__panel">
                        <StepRuntimeOptionsFields
                            stepType={step.stepType}
                            value={stepRuntimeOptions}
                            onChange={setStepRuntimeOptions}
                            onDirty={markAsChanged}
                        />
                    </Tabs.Panel>
                </Tabs>
            )}

            {!step.llmStepConfig && step.ocrStepConfig && (
                <Box className="editor-drawer__panel">
                    <OCRConfigTab
                        isLoadingProviders={isLoadingProviders}
                        isLoadingModels={isLoadingModels}
                        providers={providers}
                        models={models}
                        selectedProvider={selectedProvider}
                        modelName={ocrModelName}
                        ocrOptions={ocrOptions}
                        onProviderChange={handleProviderChange}
                        onModelChange={handleModelChange}
                        onOcrOptionsChange={withChange(setOcrOptions)}
                    />
                </Box>
            )}
        </EditorDrawer>
    );
};
